import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SubTitle from "../common/SubTitle";
import RouteSelection from "./RouteSelection";
import RouteAddDetails from "./RouteAddDetails";
import { useParams, useLocation } from "react-router-dom";
import config from "../../config.js";
import { Skeleton, Box } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { toLatLngLiteral } from "../../utils/calculateDistance";

const toStopLocation = (point, fallbackName) => {
  const coords = toLatLngLiteral(point);
  if (!coords) return null;
  return {
    id: point?.id ?? point?.stopId ?? null,
    latitude: coords.lat,
    longitude: coords.lng,
    name: point?.name || fallbackName,
  };
};

export default function RouteAddMain({
  embedded = false,
  embeddedRoute,
  onCancel,
  onComplete,
  onDelete,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [orgId, setOrgId] = useState(null);
  const [userId, setUserId] = useState(null);
  const routeToUpdate = embeddedRoute || location.state?.rowData;
  const isUpdateMode = Boolean(id || routeToUpdate);

  const [errors, setErrors] = useState({
    routeId: false,
    routeName: false,
    origin: false,
    destination: false,
  });

  const [initialTripData, setInitialTripData] = useState(() => {
    // Extract schedule data if route type is ONE_TIME_DELIVERY and schedules exist
    if (
      routeToUpdate?.routeType === "ONE_TIME_DELIVERY" &&
      routeToUpdate?.schedules?.length > 0
    ) {
      const schedule = routeToUpdate.schedules[0]; // Get first ONETIME schedule

      return {
        tripDate: dayjs(schedule.startTime),
        startTime: dayjs(schedule.startTime),
        endTime: dayjs(schedule.endTime),
      };
    }
    return null;
  });

  const validateFields = () => {
    const newErrors = {
      routeId: !routeData.routeId,
      routeName: !routeData.routeName,
      origin: !routeData.origin,
      destination: !routeData.destination,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const [routeData, setRouteData] = useState(() => {
    console.log("Initializing routeData with:", routeToUpdate);

    let organizationId = null;
    let currentUserId = null;
    try {
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        organizationId = userState.user.organizationId;
        currentUserId = userState.user.id;
        setOrgId(organizationId);
        setUserId(currentUserId);
      }
    } catch (error) {
      console.error("Error retrieving user data from localStorage:", error);
    }

    if (!routeToUpdate) {
      return {
        routeId: "",
        routeName: "",
        routeType: "UNI_DIRCTIONAL",
        routeColor: "",
        origin: null,
        destination: null,
        organizationId: organizationId,
        routePoints: [],
        selectedStops: [],
        selectedVehicles: [],
        schedules: [],
      };
    }

    const routePoints = [];
    const selectedStops = [];

    const stopsMap = {};
    const isRoundRoute = routeToUpdate.routeType === "BI_DIRECTIONAL";
    const originStopId =
      routeToUpdate.stops?.[0]?.id ?? routeToUpdate.stops?.[0]?.stopId ?? null;
    if (routeToUpdate.stops?.length > 0) {
      routeToUpdate.stops.forEach((stop, stopIndex) => {
        const stopLocation = toStopLocation(stop, stop?.name);
        if (!stopLocation) return;
        stopsMap[`${stopLocation.latitude}_${stopLocation.longitude}`] = {
          ...stop,
          ...stopLocation,
        };

        const isOrigin = stopIndex === 0;
        const isReturnToOrigin =
          originStopId != null &&
          String(stopLocation.id) === String(originStopId);
        const isDestination = stopIndex === routeToUpdate.stops.length - 1;

        if (isRoundRoute) {
          if (!isOrigin && !isReturnToOrigin) {
            selectedStops.push(stopLocation);
          }
        } else if (
          routeToUpdate.stops.length > 2 &&
          !isOrigin &&
          !isDestination
        ) {
          selectedStops.push(stopLocation);
        }
      });
    }

    if (routeToUpdate.routeCoordinates?.length > 0) {
      const midPoints =
        routeToUpdate.routeCoordinates.length > 2
          ? routeToUpdate.routeCoordinates.slice(1, -1)
          : [];

      midPoints.forEach((point, index) => {
        const coords = toLatLngLiteral(point);
        if (!coords) return;
        const coordKey = `${coords.lat}_${coords.lng}`;
        const matchingStop = stopsMap[coordKey];
        const isStop = Boolean(matchingStop);

        routePoints.push({
          lat: coords.lat,
          lng: coords.lng,
          isStop,
          stopId: isStop ? matchingStop.id : point.stopId || null,
          name: isStop ? matchingStop.name : point.name || `IM${index + 1}`,
        });
      });
    }

    let origin =
      toStopLocation(routeToUpdate.origin, "Origin") ||
      (routeToUpdate.stops?.length > 0
        ? toStopLocation(routeToUpdate.stops[0], "Origin")
        : null);

    let destination =
      toStopLocation(routeToUpdate.destination, "Destination") ||
      (routeToUpdate.stops?.length > 1
        ? toStopLocation(
            routeToUpdate.stops[routeToUpdate.stops.length - 1],
            "Destination",
          )
        : null);

    if (!origin && routeToUpdate.routeCoordinates?.length > 0) {
      origin = toStopLocation(routeToUpdate.routeCoordinates[0], "Origin");
    }

    if (!destination && routeToUpdate.routeCoordinates?.length > 1) {
      destination = toStopLocation(
        routeToUpdate.routeCoordinates[
          routeToUpdate.routeCoordinates.length - 1
        ],
        "Destination",
      );
    }

    // console.log("Extracted origin:", origin);
    // console.log("Extracted destination:", destination);
    // console.log("Extracted routePoints:", routePoints);
    // console.log("Extracted selectedStops:", selectedStops);

    return {
      routeId: routeToUpdate.routeId,
      routeName: routeToUpdate.name,
      routeType: routeToUpdate.routeType || "BI_DIRECTIONAL",
      routeColor: routeToUpdate.routeColor,
      origin,
      destination,
      organizationId: organizationId || routeToUpdate.organizationId,
      routePoints,
      selectedStops,
      selectedVehicles: routeToUpdate.vehicles || [],
      schedules: routeToUpdate.schedules || [],
    };
  });

  const [allStopsData, setAllStopsData] = useState([]);
  const [allVehiclesData, setAllVehiclesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  const path =
    id || routeToUpdate
      ? `${orgName || "organization"} > Routes > ${routeToUpdate?.name} >Update`
      : `${orgName || "organization"} > Routes > Create`;

  useEffect(() => {
    let isMounted = true;
    const fetchAllData = async () => {
      try {
        const userStateString = localStorage.getItem("userState");
        if (userStateString && !userId) {
          const userState = JSON.parse(userStateString);
          if (isMounted) {
            setOrgId(userState.user.organizationId);
            setUserId(userState.user.id);
          }
        }

        setIsLoading(true);
        await Promise.all([fetchStopsData(), fetchVehiclesData()]);
      } catch (error) {
        console.error("Error in fetchAllData:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchAllData();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  const handleRouteDataChange = (field, value) => {
    setRouteData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (value && errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const handleStopSelect = (stop) => {
    const isAlreadySelected = routeData.selectedStops.some(
      (s) => s.id === stop.id,
    );

    if (isAlreadySelected) {
      const updatedSelectedStops = routeData.selectedStops.filter(
        (s) => s.id !== stop.id,
      );
      const updatedRoutePoints = routeData.routePoints.filter(
        (p) => !(p.isStop && p.stopId === stop.id),
      );

      setRouteData((prev) => ({
        ...prev,
        selectedStops: updatedSelectedStops,
        routePoints: updatedRoutePoints,
      }));
    } else {
      const updatedSelectedStops = [...routeData.selectedStops, stop];
      const newRoutePoint = {
        lat: Number(stop.latitude),
        lng: Number(stop.longitude),
        isStop: true,
        stopId: stop.id,
        name: stop.name,
      };

      const updatedRoutePoints = [...routeData.routePoints, newRoutePoint];

      setRouteData((prev) => ({
        ...prev,
        selectedStops: updatedSelectedStops,
        routePoints: updatedRoutePoints,
      }));
    }
  };

  const fetchStopsData = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/stop/organization`, {
        withCredentials: true,
      });
      setAllStopsData(response.data);
    } catch (error) {
      console.error("Error fetching stops:", error);
      if (error.response && error.response.status === 401) {
        navigate("/");
      }
    }
  };

  const fetchVehiclesData = async () => {
    try {
      const response = await axios.get(
        `${config.baseURL}/vehicle/organization`,
        {
          withCredentials: true,
        },
      );
      console.log(response.data, "naveed test");
      setAllVehiclesData(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      if (error.response && error.response.status === 401) {
        navigate("/");
      }
    }
  };

  const handleRouteTypeChange = (type) => {
    const validTypes = [
      "BI_DIRECTIONAL",
      "UNI_DIRCTIONAL",
      "ONE_TIME_DELIVERY",
    ];

    if (!validTypes.includes(type)) {
      return;
    }

    setRouteData((prev) => {
      const newData = {
        ...prev,
        routeType: type,
      };

      // When switching to BI_DIRECTIONAL (Round Route)
      if (type === "BI_DIRECTIONAL") {
        // Set destination same as origin
        if (prev.origin) {
          newData.destination = prev.origin;
        }
      }
      // When switching from BI_DIRECTIONAL to other types
      else if (prev.routeType === "BI_DIRECTIONAL") {
        // Clear destination so user can select a different one
        newData.destination = null;
      }

      return newData;
    });
  };

  const prepareRouteCoordinates = () => {
    const coordinates = [];

    if (routeData.origin) {
      coordinates.push({
        longitude: routeData.origin.longitude,
        latitude: routeData.origin.latitude,
        isStop: true,
        stopId: routeData.origin.id,
      });
    }

    if (routeData.routePoints && routeData.routePoints.length > 0) {
      routeData.routePoints.forEach((point) => {
        coordinates.push({
          longitude: point.lng,
          latitude: point.lat,
          isStop: point.isStop || false,
          stopId: point.stopId || null,
        });
      });
    }

    if (routeData.destination) {
      coordinates.push({
        longitude: routeData.destination.longitude,
        latitude: routeData.destination.latitude,
        isStop: true,
        stopId: routeData.destination.id,
      });
    }

    return coordinates;
  };

  const handleSubmitRoute = async () => {
    const newErrors = {
      routeId: !routeData.routeId,
      routeName: !routeData.routeName,
      origin: !routeData.origin,
      destination:
        routeData.routeType === "BI_DIRECTIONAL"
          ? false
          : !routeData.destination,
      intermediateStops: false,
    };

    // Additional validation for Round Route - at least one intermediate stop required
    if (
      routeData.routeType === "BI_DIRECTIONAL" &&
      routeData.selectedStops.length === 0
    ) {
      newErrors.intermediateStops = true;
      console.log("At least one intermediate stop is required for Round Route");
    }

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return null;
    }

    try {
      const isUpdate = id || routeToUpdate;
      const routeId = isUpdate ? id || routeToUpdate.id : null;

      // For round routes, set destination same as origin
      const effectiveDestination =
        routeData.routeType === "BI_DIRECTIONAL"
          ? routeData.origin
          : routeData.destination;

      if (isUpdate) {
        const basicRouteData = {
          id: routeId,
          routeId: routeData.routeId,
          name: routeData.routeName,
          routeColor: routeData.routeColor,
          organizationId: orgId,
          performedByUserId: userId,
        };

        console.log("Updating basic route data:", basicRouteData);

        const basicUpdateResponse = await axios.put(
          `${config.baseURL}/route/`,
          basicRouteData,
          { withCredentials: true },
        );

        console.log(
          "Route basic info updated successfully:",
          basicUpdateResponse.data,
        );

        const routeCoordinates = [];

        if (routeData.origin) {
          routeCoordinates.push({
            longitude: routeData.origin.longitude,
            latitude: routeData.origin.latitude,
          });
        }

        if (routeData.routePoints && routeData.routePoints.length > 0) {
          routeData.routePoints.forEach((point) => {
            routeCoordinates.push({
              longitude: point.lng,
              latitude: point.lat,
            });
          });
        }

        if (effectiveDestination) {
          routeCoordinates.push({
            longitude: effectiveDestination.longitude,
            latitude: effectiveDestination.latitude,
          });
        }

        const coordinatesData = {
          id: routeId,
          routeType: routeData.routeType,
          routeCoordinates: routeCoordinates,
          performedByUserId: userId,
        };

        console.log("Updating coordinates data:", coordinatesData);

        const coordinatesUpdateResponse = await axios.patch(
          `${config.baseURL}/route/coordinates/`,
          coordinatesData,
          { withCredentials: true },
        );

        console.log(
          "Route coordinates updated successfully:",
          coordinatesUpdateResponse.data,
        );

        // if (
        //   routeData.selectedVehicles &&
        //   routeData.selectedVehicles.length > 0
        // ) {
        const vehicleAssignmentData = {
          routeId: routeId,
          vehicleIds: routeData.selectedVehicles.map((vehicle) => vehicle.id),
          performedByUserId: userId,
        };

        const vehicleUpdateResponse = await axios.patch(
          `${config.baseURL}/route/vehicles`,
          vehicleAssignmentData,
          { withCredentials: true },
        );

        console.log(
          "Vehicles assigned successfully:",
          vehicleUpdateResponse.data,
        );
        // }

        return { id: routeId, ...basicUpdateResponse.data };
      } else {
        const routeCoordinates = [];

        if (routeData.origin) {
          routeCoordinates.push({
            longitude: routeData.origin.longitude,
            latitude: routeData.origin.latitude,
            isStop: true,
            stopId: routeData.origin.id,
          });
        }

        if (routeData.routePoints && routeData.routePoints.length > 0) {
          routeData.routePoints.forEach((point) => {
            routeCoordinates.push({
              longitude: point.lng,
              latitude: point.lat,
              isStop: point.isStop || false,
              stopId: point.stopId || null,
            });
          });
        }

        if (effectiveDestination) {
          routeCoordinates.push({
            longitude: effectiveDestination.longitude,
            latitude: effectiveDestination.latitude,
            isStop: true,
            stopId: effectiveDestination.id,
          });
        }

        const formData = {
          routeId: routeData.routeId,
          name: routeData.routeName,
          routeType: routeData.routeType,
          routeCoordinates: routeCoordinates,
          routeColor: routeData.routeColor,
          organizationId: orgId,
          performedByUserId: userId,
        };

        console.log("Creating new route:", formData);

        const routeResponse = await axios.post(
          `${config.baseURL}/route`,
          formData,
          { withCredentials: true },
        );

        console.log("Route created successfully:", routeResponse.data);

        if (routeResponse.data && routeResponse.data.id) {
          const newRouteId = routeResponse.data.id;

          // if (
          //   routeData.selectedVehicles &&
          //   routeData.selectedVehicles.length > 0
          // ) {
          const vehicleAssignmentData = {
            routeId: newRouteId,
            vehicleIds: routeData.selectedVehicles.map((vehicle) => vehicle.id),
            performedByUserId: userId,
          };

          await axios.patch(
            `${config.baseURL}/route/vehicles`,
            vehicleAssignmentData,
            { withCredentials: true },
          );
          // }

          return routeResponse.data;
        }
        return null;
      }
    } catch (error) {
      console.error("Error submitting route:", error);
      throw error;
    }
  };

  const handleRouteSchedulesClick = () => {
    console.log("Route schedules clicked", routeData.routeId);
    navigate("/route/schedules", {
      state: {
        id: routeToUpdate,
      },
    });
  };

  const handleAssignSchedulesClick = () => {
    console.log("Assign schedules clicked", routeToUpdate);
    navigate(`/schedule/add`, {
      state: {
        updateRoute: true,
        routeId: routeToUpdate.id,
        routeName: routeToUpdate.name,
        rowData: routeToUpdate,
      },
    });
  };

  return (
    <>
      {!embedded && (
        <SubTitle
          title={t(id || routeToUpdate ? "Update Route" : "Create Routes")}
          path={path}
          add={isUpdateMode ? t("Assign Schedules") : null}
          addPath={null}
          track={isUpdateMode ? t("Route Schedules") : null}
          trackPath={null}
          onAddClick={handleAssignSchedulesClick}
          onTrackClick={handleRouteSchedulesClick}
        />
      )}
      {isLoading ? (
        <Box sx={{ p: 1 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={60}
            sx={{ mb: 3, borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={350}
            sx={{ mb: 3, borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={350}
            sx={{ mb: 3, borderRadius: 2 }}
          />
        </Box>
      ) : (
        <RouteAddDetails
          routeData={routeData}
          onRouteDataChange={handleRouteDataChange}
          stops={allStopsData}
          selectedStops={routeData.selectedStops}
          onStopSelect={handleStopSelect}
          vehicles={allVehiclesData}
          // onVehicleSelect={(vehicle) => {
          //   const isSelected = routeData.selectedVehicles.some(
          //     (v) => v.id === vehicle.id
          //   );
          //   const updatedVehicles = isSelected
          //     ? routeData.selectedVehicles.filter((v) => v.id !== vehicle.id)
          //     : [...routeData.selectedVehicles, vehicle];
          //   setRouteData((prev) => ({
          //     ...prev,
          //     selectedVehicles: updatedVehicles,
          //   }));
          // }}
          onVehicleSelect={(selectedVehicles) => {
            setRouteData((prev) => ({
              ...prev,
              selectedVehicles: selectedVehicles,
            }));
          }}
          onSubmit={handleSubmitRoute}
          errors={errors}
          routeType={routeData.routeType}
          onRouteTypeChange={handleRouteTypeChange}
          isUpdateMode={isUpdateMode}
          onRouteSchedulesClick={handleRouteSchedulesClick}
          onAssignSchedulesClick={handleAssignSchedulesClick}
          initialTripData={initialTripData}
          embedded={embedded}
          onComplete={onComplete}
          onCancel={onCancel}
          showDeleteButton={embedded && isUpdateMode && Boolean(onDelete)}
          onDelete={onDelete}
        />
      )}
    </>
  );
}
